/**
 * `/l/$secret` — a secure link on a phone (ADR-0269, BUILD_CONTRACT §7).
 * Opened from a link a module sent, with nobody signed in: no shell, no
 * company in the address. The secret is posted to secure links' public routes;
 * once the visitor is checked, the page shows the asking module's own screen
 * for that kind of link, found in the registry's `linkPages` through the
 * module's `frontend.link_pages` (ADR-0271). The screen and the owner's page
 * both download on demand.
 */

import type { LinkPageProps } from "@ken/cockpit-types";
import { SecureLinkScreen } from "@ken/module-secure_links";
import { createFileRoute, useParams } from "@tanstack/react-router";
import { Suspense, type ComponentType, type ReactElement } from "react";

import { MODULE_BY_NAME } from "../generated/modules";

export const Route = createFileRoute("/l/$secret")({
  component: SecureLinkRoute,
});

function findPage(ownerModule: string, kind: string): ComponentType<LinkPageProps> | null {
  const owner = MODULE_BY_NAME[ownerModule];
  const entry = owner?.manifest.frontend?.link_pages?.find((p) => p.kind === kind);
  if (!owner || !entry) return null;
  return owner.linkPages?.[entry.component] ?? null;
}

function SecureLinkRoute(): ReactElement {
  const { secret } = useParams({ from: "/l/$secret" });
  return (
    <Suspense fallback={null}>
      <SecureLinkScreen secret={secret} findPage={findPage} />
    </Suspense>
  );
}
